import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../components';
import { ArrowLeft } from 'lucide-react';

type LegalItem = string | { label: string; text: string };
interface LegalSection {
  title: string;
  paragraphs?: string[];
  items?: LegalItem[];
  subsections?: LegalSection[];
}

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const renderItems = (items?: LegalItem[]) =>
  items && items.length > 0 ? (
    <ul>
      {items.map((item, i) =>
        typeof item === 'string' ? (
          <li key={i}>{item}</li>
        ) : (
          <li key={i}><strong>{item.label}</strong> {item.text}</li>
        )
      )}
    </ul>
  ) : null;

export const TermsPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const intro = asArray<string>(t('terms.intro', { returnObjects: true }));
  const sections = asArray<LegalSection>(t('terms.sections', { returnObjects: true }));
  const contactLines = asArray<string>(t('terms.contactLines', { returnObjects: true }));

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/register" className="inline-flex items-center text-blue-600 hover:text-blue-500 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('terms.backToRegistration')}
        </Link>

        <Card>
          <div className="prose prose-blue max-w-none">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('terms.title')}</h1>

            <p className="text-sm text-gray-600 mb-6">{t('terms.version')}</p>

            {intro.map((p, i) => <p key={i}>{p}</p>)}

            {sections.map((section, i) => (
              <React.Fragment key={i}>
                <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">{section.title}</h2>
                {section.paragraphs?.map((p, j) => <p key={j}>{p}</p>)}
                {renderItems(section.items)}
              </React.Fragment>
            ))}

            <p>
              {t('terms.privacyRef')} <Link to="/privacy" className="text-blue-600 hover:text-blue-500">{t('terms.privacyLink')}</Link>.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">{t('terms.contactTitle')}</h2>
            <p>
              {contactLines.map((line, i) => (
                <React.Fragment key={i}>
                  {i === 0 ? <strong>{line}</strong> : line}
                  {i < contactLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
